import { StatusLozengeToneEnum } from '@modules/digital-signage/model/group-settings.model.ts';
import {
  ScheduleDayAppearanceConfig,
  ScheduleDayAppearanceEnum,
  ScheduleDayAppearanceParams,
  ScheduleLegendEnum,
  ScheduleLegendPaletteEnum,
  ScheduleStatusEnum,
  ScheduleTimeStatusConfig,
  ScheduleTimeStatusEnum
} from '@modules/digital-signage/model/playlist-schedule.model.ts';

export const SCHEDULE_STATUS_TONES: Record<
  ScheduleStatusEnum,
  StatusLozengeToneEnum
> = {
  [ScheduleStatusEnum.Active]: StatusLozengeToneEnum.Success,
  [ScheduleStatusEnum.Live]: StatusLozengeToneEnum.Brand,
  [ScheduleStatusEnum.Upcoming]: StatusLozengeToneEnum.Warning,
  [ScheduleStatusEnum.Ended]: StatusLozengeToneEnum.Neutral,
  [ScheduleStatusEnum.Draft]: StatusLozengeToneEnum.Info
};

export const SCHEDULE_STATUS_ORDER: ScheduleStatusEnum[] = [
  ScheduleStatusEnum.Draft,
  ScheduleStatusEnum.Live,
  ScheduleStatusEnum.Active,
  ScheduleStatusEnum.Upcoming,
  ScheduleStatusEnum.Ended
];

export const SCHEDULE_TIME_STATUS_CONFIG: Record<
  ScheduleTimeStatusEnum,
  ScheduleTimeStatusConfig
> = {
  [ScheduleTimeStatusEnum.Scheduled]: {
    label: 'digital_signage_schedule_status_scheduled',
    tone: null,
    cardTime: 'bg-(--gray-11) border-(--gray-7) text-(--dark-2)! font-normal!'
  },
  [ScheduleTimeStatusEnum.Live]: {
    label: 'digital_signage_schedule_status_live',
    tone: StatusLozengeToneEnum.Brand,
    cardTime:
      'bg-(--primary-100) border-(--primary) text-(--primary)! font-semibold!'
  },
  [ScheduleTimeStatusEnum.DoneToday]: {
    label: 'digital_signage_schedule_status_done_today',
    tone: StatusLozengeToneEnum.BrandSoft,
    cardTime:
      'bg-(--primary-100) border-dashed border-(--primary-400) text-(--primary)! font-normal!'
  },
  [ScheduleTimeStatusEnum.Ended]: {
    label: 'digital_signage_schedule_status_ended',
    tone: StatusLozengeToneEnum.Neutral,
    cardTime: 'bg-(--gray-10) border-(--gray-8) text-(--gray-2)! font-normal!'
  }
};

export const SCHEDULE_DAY_APPEARANCE: Record<
  ScheduleDayAppearanceEnum,
  ScheduleDayAppearanceConfig
> = {
  [ScheduleDayAppearanceEnum.Free]: {
    chip: 'bg-(--white)! border-(--gray-7)! hover:border-(--primary)!',
    chipTitle: 'text-(--dark-1)!',
    chipSubtitle: 'text-(--gray-2)!',
    cardDay: 'bg-(--white) text-(--dark-1)! font-medium!',
    legend: ScheduleLegendPaletteEnum.Default
  },
  [ScheduleDayAppearanceEnum.Idle]: {
    chip: 'bg-(--gray-11)! border-dashed! border-(--gray-6)!',
    chipTitle: 'text-(--gray-3)!',
    chipSubtitle: 'text-(--gray-4)!',
    cardDay: 'bg-(--gray-11) text-(--gray-3)! font-medium!',
    legend: ScheduleLegendPaletteEnum.Default
  },
  [ScheduleDayAppearanceEnum.Scheduled]: {
    chip: 'bg-(--primary-100)! border-(--primary)!',
    chipTitle: 'text-(--primary)!',
    chipSubtitle: 'text-(--primary-700)!',
    cardDay: 'bg-(--primary-100) text-(--primary)! font-semibold!',
    legend: ScheduleLegendPaletteEnum.Default
  },
  [ScheduleDayAppearanceEnum.Live]: {
    chip: 'bg-(--primary-100)! border-(--primary)!',
    chipTitle: 'text-(--primary)!',
    chipSubtitle: 'text-(--primary-700)!',
    cardDay:
      'bg-(--primary-100) text-(--primary)! font-semibold! outline-1 -outline-offset-1 outline-(--primary)',
    legend: ScheduleLegendPaletteEnum.Default
  },
  [ScheduleDayAppearanceEnum.DoneToday]: {
    chip: 'bg-(--primary-100)! border-dashed! border-(--primary-400)!',
    chipTitle: 'text-(--primary)!',
    chipSubtitle: 'text-(--primary-700)!',
    cardDay:
      'bg-(--primary-100) text-(--primary)! font-semibold! outline-1 outline-dashed -outline-offset-1 outline-(--primary-400)',
    legend: ScheduleLegendPaletteEnum.Default
  },
  [ScheduleDayAppearanceEnum.Ended]: {
    chip: 'bg-(--gray-10)! border-(--gray-8)!',
    chipTitle: 'text-(--gray-2)!',
    chipSubtitle: 'text-(--gray)!',
    cardDay: 'bg-(--gray-10) text-(--gray-2)! font-semibold!',
    legend: ScheduleLegendPaletteEnum.Muted
  },
  [ScheduleDayAppearanceEnum.Conflict]: {
    chip: 'bg-(--white)! border-(--red-2)!',
    chipTitle: 'text-(--red)!',
    chipSubtitle: 'text-(--red-4)!',
    cardDay: 'bg-(--white) text-(--red)! font-semibold!',
    legend: ScheduleLegendPaletteEnum.Default
  }
};

const DAY_APPEARANCE_BY_STATUS: Record<
  ScheduleTimeStatusEnum,
  ScheduleDayAppearanceEnum
> = {
  [ScheduleTimeStatusEnum.Scheduled]: ScheduleDayAppearanceEnum.Scheduled,
  [ScheduleTimeStatusEnum.Live]: ScheduleDayAppearanceEnum.Live,
  [ScheduleTimeStatusEnum.DoneToday]: ScheduleDayAppearanceEnum.DoneToday,
  [ScheduleTimeStatusEnum.Ended]: ScheduleDayAppearanceEnum.Ended
};

export const getDayAppearance = ({
  selected,
  status,
  hasConflict,
  isOutOfRange,
  isFull,
  disabled
}: ScheduleDayAppearanceParams): ScheduleDayAppearanceEnum => {
  if (status === ScheduleTimeStatusEnum.Ended) {
    return selected
      ? ScheduleDayAppearanceEnum.Ended
      : ScheduleDayAppearanceEnum.Idle;
  }
  if (selected) {
    return hasConflict || isOutOfRange
      ? ScheduleDayAppearanceEnum.Conflict
      : DAY_APPEARANCE_BY_STATUS[status ?? ScheduleTimeStatusEnum.Scheduled];
  }
  return disabled || isOutOfRange || isFull
    ? ScheduleDayAppearanceEnum.Idle
    : ScheduleDayAppearanceEnum.Free;
};

const SCHEDULE_COLORS = [
  '--red-4',
  '--blue-2',
  '--primary-400',
  '--teal',
  '--yellow'
];

export const getScheduleColor = (index: number): string =>
  `var(${SCHEDULE_COLORS[index % SCHEDULE_COLORS.length]})`;

export const SCHEDULE_LEGEND_COLORS: Record<ScheduleLegendEnum, string> = {
  [ScheduleLegendEnum.Free]: 'bg-(--gray-8)',
  [ScheduleLegendEnum.Booked]: 'bg-(--gray-3)',
  [ScheduleLegendEnum.ThisSchedule]: 'bg-(--primary)',
  [ScheduleLegendEnum.Conflict]: 'bg-(--red-4)'
};

export const SCHEDULE_LEGEND_PALETTES: Record<
  ScheduleLegendPaletteEnum,
  Record<ScheduleLegendEnum, string>
> = {
  [ScheduleLegendPaletteEnum.Default]: SCHEDULE_LEGEND_COLORS,
  [ScheduleLegendPaletteEnum.Muted]: {
    [ScheduleLegendEnum.Free]: 'bg-(--white)',
    [ScheduleLegendEnum.Booked]: 'bg-(--gray-5)',
    [ScheduleLegendEnum.ThisSchedule]: 'bg-(--gray-2)',
    [ScheduleLegendEnum.Conflict]: 'bg-(--red-4)'
  }
};
